// ⚠️ Editing this file? Read docs/legal-version-bump.md first.
// Substantive changes require bumping CURRENT_LEGAL_VERSION in
// src/shared/legal-version.js — otherwise users won't be re-prompted.

export default function OriTermsOfService() {
  return (
    <div className="min-h-screen bg-[#f9f5e9] text-ink">
      <div className="mx-auto max-w-3xl px-6 py-16 sm:px-10">
        <a
          href="/"
          className="mb-8 inline-block text-sm font-medium text-ink/70 hover:text-ink"
        >
          ← Back to Ori
        </a>

        <header className="mb-10">
          <h1
            className="mb-2 text-2xl font-bold uppercase tracking-tight"
            style={{
              fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif',
              color: '#3A331D',
            }}
          >
            Oriscen Terms of Use
          </h1>
          <p className="text-sm text-ink/60">Effective: 2026</p>
        </header>

        <Prose>
          <p>
            PLEASE READ THESE TERMS AND CONDITIONS CAREFULLY. THE TERMS AND CONDITIONS
            (“AGREEMENT”) CONSTITUTE A LEGAL AGREEMENT BETWEEN YOU AND ORISCEN, INC.
          </p>
          <p>
            SECTION 16 OF THIS AGREEMENT CONTAINS PROVISIONS THAT GOVERN HOW CLAIMS THAT YOU AND
            WE HAVE AGAINST EACH OTHER ARE RESOLVED, INCLUDING, WITHOUT LIMITATION, ANY CLAIMS
            THAT AROSE OR WERE ASSERTED BEFORE THE EFFECTIVE DATE OF THIS AGREEMENT. IN
            PARTICULAR, SECTION 16 SETS FORTH OUR ARBITRATION AGREEMENT WHICH WILL, WITH LIMITED
            EXCEPTIONS, REQUIRE DISPUTES BETWEEN US TO BE SUBMITTED TO BINDING AND FINAL
            ARBITRATION. UNLESS YOU OPT OUT OF THE ARBITRATION AGREEMENT: (1) YOU WILL ONLY BE
            PERMITTED TO PURSUE CLAIMS AND SEEK RELIEF AGAINST US ON AN INDIVIDUAL BASIS, NOT AS
            A PLAINTIFF OR CLASS MEMBER IN ANY CLASS OR REPRESENTATIVE ACTION OR PROCEEDING; AND
            (2) YOU ARE WAIVING YOUR RIGHT TO SEEK RELIEF IN A COURT OF LAW AND TO HAVE A JURY
            TRIAL ON YOUR CLAIMS. PLEASE SEE SECTION 16 FOR MORE INFORMATION REGARDING THIS
            ARBITRATION AGREEMENT, THE POSSIBLE EFFECTS OF THIS ARBITRATION AGREEMENT, AND HOW TO
            OPT OUT OF THE ARBITRATION AGREEMENT.
          </p>

          <Section title="1. Purpose">
            <p>
              Oriscen, Inc. (“Oriscen,” “Ori,” “we,” “us,” “our,”) provides an artificial
              intelligence (“AI”)-powered social agent that operates via your phone’s native
              messaging apps to help you plan and organize hangouts with friends and family.
            </p>
            <p>
              By messaging Ori or using the Ori website/mobile application and any of the
              information offered through the Ori platform (“Services”), you agree to be bound by
              these Terms of Use (“Agreement”). The success of the Services, however, depends on
              the adherence to the terms of this Agreement by you and other users (collectively,
              “you,” “your,” or “Users”). While we will do our best to enforce the terms of this
              Agreement, we cannot warrant or represent that other Users will in fact adhere to
              this Agreement, and we cannot act as insurers or accept any liability for their
              failure to do so.
            </p>
          </Section>

          <Section title="2. Eligibility">
            <p>
              By accessing or using the Services in any way, clicking on a button, messaging Ori,
              or taking similar action to signify your acceptance of this Agreement, you hereby
              represent that:
            </p>
            <ul>
              <li>
                You have read, understand, and agree to be bound by this Agreement and any future
                amendments and additions to this Agreement as published from time to time at this
                webpage or through the Services;
              </li>
              <li>
                You must be at least 18 years old or the minimum age required to consent to use
                the Services in your jurisdiction, whichever is higher;
              </li>
              <li>
                You have the authority to enter into the Agreement personally. Except as
                otherwise provided herein, if you do not agree to be bound by the Agreement, you
                may not access or use the Services; and
              </li>
              <li>
                You will comply with all applicable laws, including those of the country, state,
                and city in which you are present while using the Services.
              </li>
            </ul>
          </Section>

          <Section title="3. Registration and Access">
            <ol style={{ listStyleType: 'lower-alpha' }}>
              <li>
                <span className="underline">Registration.</span> To access our Services, we may ask
                you to create an account by messaging Ori. Your account information should be
                correct, complete and up-to-date, and you allow us to use it to communicate with
                you regarding the Services. You may not share your account credentials or make
                your account available to any third party and you are responsible for all
                activities under your account.
              </li>
              <li>
                <span className="underline">Access.</span> By entering into this Agreement, you will be granted a
                revocable license to access the Services. Your access privileges, however, are
                conditioned on your adherence to the terms of this Agreement. We reserve the
                right to temporarily deny you access to the Services or permanently terminate
                your access privileges at any time if, in our sole discretion, you have failed to
                abide by the terms of this Agreement or appear to us likely to do so (e.g., you
                are under the minimum age to use the Services). By agreeing to grant you access,
                we do not obligate ourselves to do so or to maintain the Services, or to maintain
                it in its present form, and we expressly reserve the right to modify, suspend, or
                terminate your access privileges.
              </li>
              <li>
                <span className="underline">Prohibited Uses.</span> You understand, acknowledge and agree that any
                access or use of the Services shall be for your personal, non-commercial use
                only, and that you will not commercially exploit any portion of the Services.
              </li>
              <li>
                <span className="underline">Privileges Nontransferable.</span> Your access privileges may not be
                transferred to any third parties.
              </li>
              <li>
                <span className="underline">Passwords and Security.</span> You agree not to disclose to anyone your
                confidential password and to notify us immediately if there has been a breach of
                your security that affects our Services.
              </li>
            </ol>
          </Section>

          <Section title="4. Acceptable Use Policy">
            <p>By using the Services, you agree that:</p>
            <ol style={{ listStyleType: 'lower-alpha' }}>
              <li>
                You will only use the Services for lawful purposes, and not for deceptive or
                fraudulent purposes, and you will not send or store any unlawful material.
              </li>
              <li>You will not use the Services to cause nuisance, annoyance, or inconvenience.</li>
              <li>
                You will not use the Services, or any content accessible through the Services, for
                any commercial purpose, including but not limited to contacting, advertising to,
                soliciting or selling to any Users.
              </li>
              <li>You will not violate publicity or privacy rights of another individual.</li>
              <li>
                You will not copy or distribute any content displayed through the Services, except
                to share social plans with other Users as prompted by Ori.
              </li>
              <li>
                You will not create or compile, directly, or indirectly, any collection,
                compilation, or other directory from any content displayed through the Services
                except for your personal, non-commercial use.
              </li>
              <li>
                The information you provide to us or otherwise communicate with us is accurate.
              </li>
              <li>
                You will not use the Services in any way that could damage, disable, overburden,
                or impair any of our servers, or the networks connected to any of our servers.
              </li>
              <li>
                You will not attempt to gain unauthorized access to any part of the Services
                and/or to any service, account, resource, computer system and/or network
                connected to any of our servers.
              </li>
              <li>
                You will not deep link to the Services or access the Services manually or with any
                robot, spider, web crawler, extraction software, automated processes and/or device
                to scrape, copy, or monitor any portion of the Services or any content on the
                Services.
              </li>
              <li>
                You will report any errors, bugs, unauthorized access methodologies or any breach
                of our intellectual property rights that you discover in your use of the Services.
              </li>
              <li>
                You will not impersonate another person, act as another entity without
                authorization, or create multiple accounts.
              </li>
              <li>
                Content submitted by you or you authorize Ori to collect (“User Content”) does not
                contain material that solicits personal information from anyone under 18 or
                exploits people under 18 in a violent or sexual manner and does not violate any
                federal or state laws concerning child pornography or otherwise intended to protect
                the health or wellbeing of minors.
              </li>
              <li>
                Your User Content does not violate any state or federal law designed to regulate
                electronic advertising.
              </li>
              <li>
                Your User Content does not contain pictures, data, audio, or visual files, or any
                other content that is excessive in size, as determined by Ori in our sole
                discretion.
              </li>
              <li>
                Your User Content will not contain any material deemed illegal or inappropriate,
                as determined by Oriscen in our sole discretion, and that you and/or your User
                Content may be subject to remedial actions if found to violate this Agreement.
              </li>
            </ol>
          </Section>

          <Section title="5. Information on our Services">
            <p>
              While we will always use our best efforts to ensure the accuracy and completeness of
              information provided on our Services, we cannot guarantee the accuracy, adequacy,
              quality or suitability of any data on our Services and expressly disclaim liability
              for errors and omissions in the contents of our Services. Any use or reliance on any
              content or materials posted via the Services or obtained by you through the Services
              is at your own risk. Any link to a website or phone number owned by a third party
              does not constitute an endorsement, approval, association, sponsorship, or
              affiliation with the linked site or phone number.
            </p>
          </Section>

          <Section title="6. Artificial Intelligence Model Terms">
            <p>
              Some of our Services, including Services intended for individual Users, may include
              access to Ori’s artificial intelligence powered interface. This interface allows
              Users to message Ori through their phone’s native messaging app to help organize
              hangouts and plans with friends and family.
            </p>
            <ol style={{ listStyleType: 'lower-alpha' }}>
              <li>
                <span className="underline">Ori’s AI Content.</span> You may provide input to Ori (“input”) and
                receive output from Ori based on the input (“output”), or instruct Ori to act on
                your behalf based on your input. Input and output are collectively referred to as
                “AI Content.” You represent and warrant that you have all rights, licenses, and
                permissions needed to provide input to Ori, including to integrate with
                third-party services, to share AI Content with third parties at your direction or
                act on your behalf. As between you and Oriscen, and to the extent permitted by
                applicable law, you retain all rights in the inputs and outputs. Subject to your
                compliance with this Agreement, we assign to you all of our right, title, and
                interest—if any—in outputs.
              </li>
              <li>
                <span className="underline">Accuracy.</span> We are constantly working to improve Ori to make the
                product more accurate and reliable. However, given the probabilistic nature of
                machine learning and artificial intelligence, use of Ori may, in some situations,
                result in output that does not accurately reflect the current law, factual
                situations, and more.
              </li>
              <li>
                <span className="underline">Similarity of Output.</span> You acknowledge and understand that due to
                the nature of our Services and artificial intelligence generally, outputs may not
                be unique, and other Users may receive similar outputs from our Services. Outputs
                that are requested by or generated for other Users are not considered your output.
              </li>
              <li>
                <span className="underline">Our Use of Input.</span> We may use AI Content to provide, maintain,
                develop, train, and improve Ori, which may include complying with applicable law,
                enforcing our policies, and keeping our Services, including Ori, safe. In addition,
                we may review submitted input and the output generated to improve the quality of
                our Services and Ori.
                <p className="mt-4">
                  In addition, when you use Ori, including any artificial intelligence features,
                  you understand and agree that:
                </p>
                <ul>
                  <li>
                    We do not warrant or guarantee the accuracy and validity of Ori’s output, and it
                    is your responsibility to validate the accuracy and validity of Ori’s output.
                  </li>
                  <li>
                    You will not rely on output from Ori as a sole source of truth or factual
                    information, or as a substitute for professional services. You are responsible
                    for evaluating outputs in light of your intended use case.
                  </li>
                  <li>
                    You will evaluate Ori output for accuracy and appropriateness for your use case,
                    including using human review as appropriate, before using or sharing output from
                    Ori.
                  </li>
                  <li>
                    You will not provide, share, or otherwise use confidential information, including
                    personal information, in your input to Ori.
                  </li>
                  <li>
                    You will not use the outputs from Ori to provide tailored professional advice,
                    including therapeutic or medical advice, without review by a qualified
                    professional.
                  </li>
                  <li>You will not represent that outputs are human generated when they are not.</li>
                  <li>
                    You will not use any part of the Services, including any outputs from Ori to
                    develop any artificial intelligence (or similar) models, unless you receive our
                    express consent.
                  </li>
                  <li>
                    You will not automatically or programmatically extract data or outputs from our
                    Services.
                  </li>
                  <li>
                    You will not interfere with or disrupt our Services, including circumventing any
                    rate limits or restrictions, bypassing any protective measures or safety
                    mitigations we put on our Services, or initiating or facilitating content-based
                    attacks or jailbreaking.
                  </li>
                </ul>
              </li>
            </ol>
          </Section>

          <Section title="7. User Content">
            <p>
              We may provide you with interactive opportunities through the Services. You
              represent and warrant that you are the owner of, or otherwise have the right to
              provide, all User Content that you submit, post, transmit and/or otherwise authorize
              Ori to collect through the Services. You hereby grant us a perpetual, irrevocable,
              transferrable, full paid, royalty-free, non-exclusive, worldwide, fully sublicensable
              right and license to use, copy, display, publish, modify, remove, publicly perform,
              translate, create derivative works, distribute and/or otherwise use the User Content
              in connection with our business and in all forms now known or hereafter invented,
              without notification to and/or approval by you, except as otherwise required by law.
            </p>
            <p>
              <span className="underline">Feedback.</span> You agree that any submission of any ideas, suggestions,
              and/or proposals to us through our suggestion, feedback, or similar pages
              (“Feedback”) is at your own risk, and we have no obligations (including without
              limitations, obligations of confidentiality) with respect to such Feedback.
            </p>
          </Section>

          <Section title="8. Mobile Terms">
            <p>
              This section contains the terms and conditions (the “Mobile Terms”) for Ori’s mobile
              messaging services (the “Mobile Services”). By consenting to these Mobile Terms, you
              authorize us and our service providers to contact and/or text you at the phone
              number you provided to Ori. This authorization includes using automated dialing
              technology to text you for marketing or advertising purposes, which may include text
              notifications, and for transactional and relationship purposes, including requests
              or reviews from us. You agree and understand that by consenting to receive marketing
              or advertising text messages, we may text you at the phone number you provided to
              us, even if your phone number is registered on any state or federal do-not-call
              list.
            </p>
            <p>
              We may modify or cancel the Mobile Services or any of its features without notice.
              To the extent permitted by applicable law, we may also modify these Mobile Terms at
              any time and your continued use of the Mobile Services following the effective date
              of any such changes shall constitute your acceptance of such changes.
            </p>
            <p>
              We do not charge for the Mobile Services, but you are responsible for all charges
              related to SMS and/or text messages, including charges from your wireless provider.
              Message frequency varies. Message and data rates may apply. Check your mobile plan
              and contact your wireless provider for details.
            </p>
            <p>
              You may opt out of the Mobile Services at any time. To do so, text the single
              keyword command STOP in response to any text message from Ori, click the unsubscribe
              link (where available) in any text message from us, or contact us directly and ask
              us to opt you out using the information at the bottom of this Agreement. You may
              receive a one-time opt-out confirmation text message. No further messages will be
              sent to your mobile device, unless initiated by you. For service support or
              assistance, text HELP in response to one of our text messages or contact us directly
              using the information at the bottom of this Agreement.
            </p>
            <p>
              We may change any short code or telephone number we use to operate the Mobile
              Services at any time and will notify you of these changes. You acknowledge that any
              messages, including any STOP or HELP requests, you send to a short code or telephone
              number we have changed may not be received and we will not be responsible for
              honoring requests made in such messages.
            </p>
            <p>
              The wireless carriers supported by the Mobile Services are not liable for delayed or
              undelivered messages. You agree to provide us with a valid mobile number. If you get
              a new mobile number, you will need to sign up for the Mobile Services with your new
              number.
            </p>
            <p>
              To the extent permitted by applicable law, you agree that we will not be liable for
              failed, delayed, or misdirected delivery of any information sent through the Mobile
              Services, any errors in such information, and/or any action you may or may not take
              in reliance on the information or Mobile Services.
            </p>
          </Section>

          <Section title="9. Subscriptions, Plans, Payment and Cancelation">
            <p>
              To access or use some of our Services, you may need to subscribe to recurring
              payment (our “Subscription Services”). If you sign up for Subscription Services, you
              must provide us with valid payment information.
            </p>
            <ol style={{ listStyleType: 'lower-alpha' }}>
              <li>
                <span className="underline">Free Trial.</span> If you sign up for a free trial of
                our Subscription Services, you will not be automatically charged at the conclusion
                of the trial. By signing up for a free trial, you agree that we may send you
                communications related to your trial and other company updates, promotions, and
                service announcements.
              </li>
              <li>
                <span className="underline">Subscription Term; Auto Renewal.</span> Subscriptions
                are normally assessed on a monthly or annual basis. Subscription fees are
                calculated from the day upon which your paid subscription commences. Your
                subscription to the Subscription Services shall be for the initial term (e.g.,
                monthly or annually) as selected by you.
              </li>
            </ol>
            <p>
              <strong>
                PAYMENTS AUTOMATICALLY RENEW FOR INDEFINITE SUCCESSIVE RENEWAL TERMS FROM THE SAME
                PERIOD AS THE INITIAL TERM, UNTIL CANCELED BY YOU OR BY US IN ACCORDANCE WITH THESE
                TERMS.
              </strong>
            </p>
            <ol style={{ listStyleType: 'lower-alpha' }} start={3}>
              <li>
                <span className="underline">Right to Modify Pricing.</span> We reserve the right to
                modify the fee of our Subscription Services, and to create additional tiers or
                types of Subscription Services, including subscription fee tiers, at any time.
              </li>
              <li>
                <span className="underline">Cancelation.</span> You may cancel your subscription,
                without penalty or obligations, at any time prior to midnight of the third business
                day following the date you subscribed. In the event that you die before the end of
                your subscription period, your estate shall be entitled to a refund of that portion
                of any payment you had made for your subscription which is allocable to the period
                after your death. In the event that you become disabled (such that you are unable
                to use our Services) before the end of your subscription period, you shall be
                entitled to a refund of that portion you had made for your subscription which is
                allocable to the period after your disability by providing us notice in the same
                manner as you request a refund in the “Refunds” section below.
                <p className="mt-4">
                  Notwithstanding the foregoing, either we or you may cancel your subscription at
                  any time and for any reason, but you must send us an explicit request at least 48
                  hours before the next order process date. In the event of a cancelation by us or
                  you, all fees due to us up to the end of the then-current billing cycle at time of
                  cancelation shall remain payable to us. Users may cancel their subscription by:
                </p>
                <ul>
                  <li>
                    Navigating to the profile settings within the Ori app and following the prompts
                    to cancel your account.
                  </li>
                  <li>
                    Texting Ori from your registered account phone number and asking to cancel the
                    account.
                  </li>
                  <li>
                    Emailing a clear and specific cancelation request email to{' '}
                    <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>.
                  </li>
                </ul>
                <p className="mt-4">
                  Cancelation in the middle of a subscription term is deemed a waiver of any balance
                  of the service term remaining, and you may not retrieve any data that you may have
                  stored with us. You further agree to pay invoices promptly, and, in any event, no
                  later than 30 days from the date of the invoice. Failure to timely pay an invoice
                  gives us the right to, at our discretion, either terminate your service, or charge
                  any payment method we hold on to your account for the full balance of any
                  indebtedness to us. In addition, any discounts granted will be revoked, and
                  payment on the full non-discounted value of Services sold under an invoice not
                  paid within 30 days will then be due.
                </p>
              </li>
            </ol>
            <ol style={{ listStyleType: 'lower-alpha' }}>
              <li>
                <span className="underline">Refunds.</span> If you would like to request a refund,
                please contact Oriscen with your order number (see confirmation email) by mailing
                or delivering a signed and dated notice which states that you, the buyer, are
                canceling this Agreement, or words of similar effect. Please also include your
                email, phone number or other identifier associated with your account, along with
                your order number. This notice shall be sent to: Oriscen, Inc. 2261 Market Street,
                STE 86857, San Francisco, CA 94114. California and Ohio Users may also email Oriscen
                at <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>.
              </li>
              <li>
                <span className="underline">Termination.</span> If at any time we believe, in our
                sole discretion, that you may have violated any provision of this Agreement, we may
                immediately terminate your access to the Subscription Services without any refund or
                other remedy, and all fees due to us up to the end of the then-current billing cycle
                at the time of such termination shall remain payable to us. Such termination will
                not limit any other right by us under contract, tort, or any other legal theory to
                pursue any claim or cause of action against you for violating this Agreement,
                including without limitation monetary damages, injunctive relief, attorney’s fees,
                and court costs.
              </li>
            </ol>
          </Section>

          <Section title="10. Intellectual Property Ownership">
            <p>
              We (and our licensors, where applicable) shall own all right, title, and interest,
              including all related intellectual property rights, in and to the Services. This
              Agreement is not a sale and does not convey to you any rights of ownership in or
              related to the Services, or any intellectual property rights owned by us. Our name,
              logo, and the product names associated with the Services are our trademarks or belong
              to third parties, and no right or license is granted to use them. You agree that you
              will not remove, alter or obscure any copyright, trademark, service mark or other
              proprietary rights notices incorporated in or accompanying the Services.
            </p>
          </Section>

          <Section title="11. Privacy">
            <p>
              We have adopted a privacy policy outlining our personal information collection and
              use practices. Please refer to our <a href="/privacy">Privacy Policy</a> for details
              about how we collect and use personal information.
            </p>
          </Section>

          <Section title="12. Third-Party Interactions">
            <p>
              The Services may use or be used in association with third-party content (“Third-Party
              Content”). Such Third-Party Content are not under our control. We are not responsible
              for Third-Party Content, while you may use Third-Party Content, services, and
              integrations. We do not review, approve, monitor, endorse, warrant or make any
              representations with respect to Third-Party Content or their products or services.
              When you click on a link to any Third-Party Content, we will not warn you that you
              have left our Services and will not warn you that you are subject to the terms and
              conditions (including privacy policies) of another website or destination. You use
              Third-Party Content at your own risk. You should review the applicable terms and
              policies, including privacy and data gathering practices of any Third-Party Content,
              and make whatever investigation you feel necessary or appropriate before proceeding
              with any transaction with any third party.
            </p>
            <p>
              <strong>Google Maps Platform.</strong> Our Services use Google Maps Platform to
              display place information, imagery, and reviews. By using our Services, you agree
              to be bound by the{' '}
              <a
                href="https://cloud.google.com/maps-platform/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Maps Platform Terms of Service
              </a>{' '}
              (which incorporate the{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                Google Terms of Service
              </a>
              ), which are incorporated herein by reference. Place details, photos, and reviews
              shown through the Services are provided by Google and its content contributors and
              are displayed with the attributions Google requires. The underlying reviews are
              authored by third parties and are not verified or endorsed by us.
            </p>
          </Section>

          <Section title="13. Indemnification">
            <p>
              You agree to indemnify and hold harmless Oriscen and its officers, directors,
              employees, agents and affiliates (each, an “Indemnified Party”), from and against any
              losses, claims, actions, costs, damages, penalties, fines and expenses, including
              without limitation attorneys’ fees and expenses, that may be incurred by an
              Indemnified Party arising out of, relating to or resulting from (a) your input and
              User Content; (b) your misuse of the Services; (c) your violation of this Agreement;
              or (d) your violation of any applicable laws, rules or regulations through or related
              to the use of the Services. In the event of any claim, allegation, suit or proceeding
              alleging any matter potentially covered by the agreements in this Section, you agree
              to pay for the defense of the Indemnified Party, including reasonable costs and
              attorneys’ fees incurred by the Indemnified Party. We reserve the right, at our own
              cost, to assume the exclusive defense and control of any matter otherwise subject to
              indemnification by you, in which event you will fully cooperate with us in asserting
              any available defenses. This provision does not require you to indemnify any
              Indemnified Party for any unconscionable commercial practice by such party, or for
              such party’s negligence, fraud, deception, false promise, misrepresentation or
              concealment, suppression or omission of any material fact in connection with the
              Services. You agree that the provisions in this Section will survive any termination
              of your account, this Agreement, or your access to the Services.
            </p>
          </Section>

          <Section title="14. Disclaimer of Warranties">
            <p>
              YOU EXPRESSLY UNDERSTAND AND AGREE THAT TO THE FULLEST EXTENT OF THE LAW, YOUR USE OF
              THE SERVICES IS ENTIRELY AT YOUR OWN RISK. CHANGES ARE PERIODICALLY MADE TO THE
              SERVICES AND MAY BE MADE AT ANY TIME WITHOUT NOTICE TO YOU. THE SERVICES ARE PROVIDED
              ON AN “AS IS” BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO, WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE AND NON-INFRINGEMENT. WE MAKE NO WARRANTIES OR REPRESENTATIONS ABOUT THE
              ACCURACY, RELIABILITY, COMPLETENESS OR TIMELINESS OF THE CONTENT MADE AVAILABLE
              THROUGH THE SERVICES, OR THE TEXT, GRAPHICS, OR LINKS.
            </p>
            <p>
              WE DO NOT WARRANT THAT THE SERVICES WILL OPERATE ERROR-FREE OR THAT THE SERVICES ARE
              FREE OF COMPUTER VIRUSES AND OTHER HARMFUL MALWARE. IF YOUR USE OF THE SERVICES
              RESULTS IN THE NEED FOR SERVICING OR REPLACING EQUIPMENT OR DATA, WE SHALL NOT BE
              RESPONSIBLE FOR THOSE ECONOMIC COSTS.
            </p>
            <p>
              ANY USE OF OUTPUTS FROM ORI’S ARTIFICIAL INTELLIGENCE MODEL IS AT YOUR SOLE RISK AND
              YOU WILL NOT RELY ON OUTPUTS AS A SOLE SOURCE OF TRUTH OR FACTUAL INFORMATION, OR AS A
              SUBSTITUTE FOR PROFESSIONAL ADVICE.
            </p>
          </Section>

          <Section title="15. Limitation of Liability">
            <p>
              UNDER NO CIRCUMSTANCES AND UNDER NO LEGAL THEORY (WHETHER IN CONTRACT, TORT, OR
              OTHERWISE) SHALL ORISCEN BE LIABLE TO YOU OR ANY THIRD PARTY FOR (A) ANY INDIRECT,
              INCIDENTAL, SPECIAL, EXEMPLARY, CONSEQUENTIAL OR PUNITIVE DAMAGES, INCLUDING LOST
              PROFITS, LOST SALES OR BUSINESS, LOST DATA OR DATA BREACH, OR (B) FOR ANY DIRECT
              DAMAGES, COSTS, LOSSES OR LIABILITIES (INCLUDING ATTORNEYS’ FEES) IN EXCESS OF THE
              FEES ACTUALLY PAID BY YOU IN THE TWO (2) MONTHS PRECEDING THE EVENT GIVING RISE TO
              YOUR CLAIM OR, IF NO FEES APPLY, ONE HUNDRED ($100) U.S. DOLLARS. THE PROVISIONS OF
              THIS SECTION ALLOCATE THE RISKS UNDER THIS AGREEMENT BETWEEN THE PARTIES, AND THE
              PARTIES HAVE RELIED ON THESE LIMITATIONS IN DETERMINING WHETHER TO ENTER INTO THIS
              AGREEMENT. SOME STATES DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES OR LIMITATION
              OF LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES, WHICH MEANS THAT SOME OF THE
              ABOVE LIMITATIONS MAY NOT APPLY TO YOU. IN THESE STATES, OUR LIABILITY WILL BE
              LIMITED TO THE GREATEST EXTENT PERMITTED BY LAW.
            </p>
            <p>
              WE MAKE NO REPRESENTATIONS OR WARRANTIES WITH RESPECT TO THE SERVICES, OR ANY OTHER
              ITEMS OR SERVICES PROVIDED BY US, INCLUDING, WITHOUT LIMITATION, ANY IMPLIED WARRANTY
              ARISING BY USAGE OF TRADE, COURSE OF DEALING OR COURSE OF PERFORMANCE, ANY IMPLIED
              WARRANTY OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE AND ANY IMPLIED
              WARRANTY OF NON-INFRINGEMENT. YOU ACKNOWLEDGE THAT THE SERVICES (INCLUDING ANY
              SERVERS OR OTHER HARDWARE, SOFTWARE AND ANY OTHER ITEMS USED OR PROVIDED BY US IN
              CONNECTION WITH THE SERVICES) ARE PROVIDED "AS IS" AND THAT WE MAKE NO WARRANTY THAT
              THE SERVICES WILL BE FREE FROM BUGS, FAULTS, DEFECTS OR ERRORS OR THAT ACCESS TO THE
              SERVICES WILL BE UNINTERRUPTED.
            </p>
          </Section>

          <Section title="16. Dispute Resolution">
            <p>
              PLEASE READ THE FOLLOWING SECTION CAREFULLY. IT REQUIRES YOU TO ARBITRATE DISPUTES
              WITH US AND LIMITS THE MANNER IN WHICH YOU CAN SEEK RELIEF. THIS SECTION 16 OF THIS
              AGREEMENT SHALL BE REFERRED TO AS THE “ARBITRATION AGREEMENT.”
            </p>
            <ol style={{ listStyleType: 'lower-alpha' }}>
              <li>
                <span className="underline">Scope of the Arbitration Agreement.</span> You agree that any dispute or
              claim relating in any way to your access or use of the Services or as a consumer of
              our Services, to any advertising or marketing communications regarding us or our
              Services, to any products or services sold or distributed through the Services that
              you received as a consumer, or to any aspect of your relationship or transactions
              with us as a consumer of our Services will be resolved by binding arbitration, rather
              than in court, except that (1) you may assert claims in a small claims court if your
              claims qualify, so long as the matter remains in such court and advances only on an
              individual (non-class, non-representative) basis; and (2) you or Oriscen may seek
              equitable relief in court for infringement or other misuse of intellectual property
              rights (such as trademarks, trade dress, domain names, trade secrets, copyrights, and
              patents). This Arbitration Agreement shall apply, without limitation, to all claims
              that arose or were asserted before the Effective Date of this Agreement. IF YOU AGREE
              TO ARBITRATION WITH ORISCEN, YOU ARE AGREEING IN ADVANCE THAT YOU WILL NOT
              PARTICIPATE IN OR SEEK TO RECOVER MONETARY OR OTHER RELIEF IN ANY SUCH CLASS,
              COLLECTIVE, AND/OR REPRESENTATIVE LAWSUIT. INSTEAD, BY AGREEING TO ARBITRATION, YOU
              MAY BRING YOUR CLAIMS AGAINST US IN AN INDIVIDUAL ARBITRATION PROCEEDING. IF
              SUCCESSFUL ON SUCH CLAIMS, YOU COULD BE AWARDED MONEY OR OTHER RELIEF BY AN
              ARBITRATOR.
              </li>
              <li>
                <span className="underline">Informal Resolution.</span> You and Oriscen agree that good-faith informal
              efforts to resolve disputes often can result in a prompt, low-cost and mutually
              beneficial outcome. You and Oriscen therefore agree that, before either you or
              Oriscen demands arbitration against the other, we will personally meet and confer,
              via telephone or videoconference, in a good-faith effort to resolve informally any
              claim covered by this mutual Arbitration Agreement. If you are represented by
              counsel, your counsel may participate in the conference, but you shall also fully
              participate in the conference. The party initiating the claim must give notice to the
              other party in writing of its, his, or her intent to initiate an informal dispute
              resolution conference, which shall occur within 60 days after the other party
              receives such notice, unless an extension is mutually agreed upon by the parties. To
              notify Oriscen that you intend to initiate an informal dispute resolution conference,
              email <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>, providing your
              username associated with your Ori account (if any), the email address associated with
              your Ori account (if any), and a description of your claim. In the interval between
              the party receiving such notice and the informal dispute resolution conference, the
              parties shall be free to attempt to resolve the initiating party’s claims. Engaging
              in an informal dispute resolution conference is a requirement that must be fulfilled
              before commencing arbitration. The statute of limitations and any filing fee
              deadlines shall be tolled while the parties engage in the informal dispute resolution
              process required by this paragraph. If you and Oriscen do not resolve any dispute by
              informal negotiation within 60 days of the initial informal dispute resolution
              conference, then either of the parties may, by notice to the other, demand mediation
              under the supervision of JAMS, in accordance with this Arbitration Agreement.
              </li>
              <li>
                <span className="underline">Arbitration Rules and Forum.</span> This Arbitration Agreement is governed
              by the Federal Arbitration Act in all respects. To begin an arbitration proceeding,
              you must send a letter requesting arbitration and describing your claim to our
              registered agent at Oriscen, Inc. 2261 Market Street STE 86857, San Francisco, CA
              94114. The arbitration will be conducted by JAMS under its rules and pursuant to the
              terms of this Agreement. Disputes involving claims and counterclaims under $250,000,
              not inclusive of attorneys’ fees and interest, shall be subject to JAMS’s most
              current version of the Streamlined Arbitration Rules and procedures available at{' '}
              <a
                href="http://www.jamsadr.com/rules-streamlined-arbitration/"
                target="_blank"
                rel="noopener noreferrer"
              >
                http://www.jamsadr.com/rules-streamlined-arbitration/
              </a>
              ; all other claims shall be subject to JAMS’s most current version of the
              Comprehensive Arbitration Rules and Procedures, available at{' '}
              <a
                href="http://www.jamsadr.com/rules-comprehensive-arbitration/"
                target="_blank"
                rel="noopener noreferrer"
              >
                http://www.jamsadr.com/rules-comprehensive-arbitration/
              </a>
              . JAMS’s rules are also available at{' '}
              <a href="https://www.jamsadr.com" target="_blank" rel="noopener noreferrer">
                www.jamsadr.com
              </a>{' '}
              (under the Rules &amp; Clauses tab) or by calling JAMS at 800-352-5267. Payment of all
              filing, administration, and arbitration fees will be governed by JAMS’s rules. If the
              arbitrator finds that you cannot afford to pay JAMS’s filing, administrative, hearing
              and/or other fees and cannot obtain a waiver of fees from JAMS, we will pay them for
              you. In addition, we will reimburse all such JAMS’s filing, administrative, hearing
              and/or other fees for claims with an amount in controversy totaling less than $10,000.
              If JAMS is not available to arbitrate, the parties will select an alternative arbitral
              forum. You may choose to have the arbitration conducted by telephone, video
              conference, based on written submissions, or in person in the county where you live or
              at another mutually agreed location.
              </li>
              <li>
                <span className="underline">Arbitrator Powers.</span> The arbitrator, and not any federal, state, or
              local court or agency, shall have exclusive authority to resolve any dispute relating
              to the interpretation, applicability, enforceability or formation of this Arbitration
              Agreement including, but not limited to any claim that all or any part of this
              Arbitration Agreement is void or voidable. The arbitration will decide the rights and
              liabilities, if any, of you and Oriscen. Notwithstanding the class-action waiver in
              this Arbitration Agreement, if multiple individual arbitration demands are filed that
              involve materially similar or overlapping facts or legal issues and are submitted by
              the same counsel or coordinated counsel, either party may request that JAMS or the
              arbitrator implement administrative coordination procedures to promote efficiency.
              Such procedures may include: (1) assignment of related matters to the same
              arbitrator; (2) coordinated or consolidated scheduling, discovery, or motion practice;
              (3) joint resolution of common issues of law or fact; and (4) any other procedures
              permitted under applicable law and the JAMS Rules that reduce duplication while
              preserving the individual nature of each claim, including but not limited to batching.
              The arbitrator will have the authority to grant motions dispositive of all or part of
              any claim or dispute. The arbitrator will have the authority to award monetary damages
              and to grant any non-monetary remedy or relief available to an individual under
              applicable law, the arbitral forum’s rules, and this Agreement (including this
              Arbitration Agreement). The arbitrator will issue a written statement of decision
              describing the essential findings and conclusions on which any award (or decision not
              to render an award) is based, including the calculation of any damages awarded. The
              arbitrator shall follow the applicable law. The arbitrator has the same authority to
              award relief on an individual basis that a judge in a court of law would have. The
              arbitrator’s decision is final and binding on you and Oriscen.
              </li>
              <li>
                <span className="underline">Waiver of Jury Trial.</span> YOU AND ORISCEN WAIVE ANY CONSTITUTIONAL AND
              STATUTORY RIGHTS TO SUE IN COURT AND RECEIVE A JUDGE OR JURY TRIAL. You and Oriscen
              are instead electing to have claims and disputes resolved by arbitration, except as
              specified in section 16(a), above. There is no judge or jury in arbitration, and court
              review of an arbitration award is limited.
              </li>
              <li>
                <span className="underline">Waiver of Class or Consolidated Actions.</span> YOU AND ORISCEN AGREE TO
              WAIVE ANY RIGHT TO RESOLVE CLAIMS WITHIN THE SCOPE OF THIS ARBITRATION AGREEMENT ON A
              CLASS, COLLECTIVE, OR REPRESENTATIVE BASIS. ALL CLAIMS AND DISPUTES WITHIN THE SCOPE OF
              THIS ARBITRATION AGREEMENT MUST BE ARBITRATED ON AN INDIVIDUAL BASIS AND NOT ON A CLASS
              BASIS. CLAIMS OF MORE THAN ONE CUSTOMER OR USER CANNOT BE ARBITRATED OR LITIGATED
              JOINTLY OR CONSOLIDATED WITH THOSE OF ANY OTHER CUSTOMER OR USER. If, however, this
              waiver of class or consolidated actions is deemed invalid or unenforceable with
              respect to a particular claim or dispute, neither you nor Oriscen is entitled to
              arbitration of such claim or dispute. Instead, all such claims and disputes will then
              be resolved in court as set forth in section 17.
              </li>
              <li>
                <span className="underline">Bellwether Process.</span> If ten (10) or more similar arbitration demands
              are filed by the same counsel or coordinated counsel, the arbitrator may direct that a
              reasonable number of individual cases proceed first as bellwether matters, with the
              remaining cases stayed pending those outcomes and any associated filing or
              administrative fees deferred until the stay is lifted. These procedures are solely for
              administrative efficiency and do not authorize class, collective, representative, or
              aggregated proceedings; each claim must remain an individual arbitration. No
              arbitration may be conducted as a class, collective, representative, or aggregated
              proceeding, unless expressly authorized in this Arbitration Agreement, and no
              arbitration will be combined with another for purposes of adjudicating the merits of
              multiple claims together without the prior written consent of all parties.
              </li>
              <li>
                <span className="underline">Opt Out.</span> You may opt out of this Arbitration Agreement. If you do so,
              neither you nor Oriscen can force the other to arbitrate as a result of this
              Agreement. To opt out, you must notify Oriscen in writing no later than 30 days after
              first becoming subject to this Arbitration Agreement. Your notice must include your
              name and address, your email address (if you have one), and a CLEAR statement that you
              want to opt out of this Arbitration Agreement. You must send your opt-out notice to{' '}
              <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>. If you opt out of this
              Arbitration Agreement, all other parts of this Agreement will continue to apply to
              you. Opting out of this Arbitration Agreement has no effect on any other arbitration
              agreements that you have entered into with us or may enter into in the future with us.
              </li>
              <li>
                <span className="underline">Survival.</span> This Arbitration Agreement will survive any termination of
              your relationship with us.
              </li>
              <li>
                <span className="underline">Modification.</span> Notwithstanding any provision in the Agreement to the
                contrary, we agree that if we make any future material change to this Arbitration
                Agreement, it will not apply to any individual claim(s) that you had already provided
                notice of to us.
              </li>
            </ol>
          </Section>

          <Section title="17. Exclusive Venue">
            <p>
              To the extent the parties are permitted under this Agreement to initiate litigation
              in a court, both you and Oriscen agree that all claims and disputes arising out of or
              relating to the Agreement will be litigated exclusively within the State of
              California for courts situated in Santa Clara County, California, or in federal court
              for the Northern District of California.
            </p>
          </Section>

          <Section title="18. Termination">
            <p>
              You may stop accessing the Services at any time. We may modify, suspend or terminate
              your access to the Services, if we reasonably believe that you have breached this
              Agreement, or if we must do so to comply with the law. In addition to suspending or
              terminating your access to the Services, we reserve the right to take appropriate
              legal action, including without limitation pursuing civil, criminal or injunctive
              redress. Even after your right to use the Services is terminated, this Agreement will
              remain enforceable against you. All provisions which by their nature should survive to
              give effect to those provisions shall survive the termination of this Agreement.
            </p>
            <p>
              At our sole discretion, we may modify or discontinue the Services for any reason,
              with or without notice to you and without liability to you or any third party.
            </p>
          </Section>

          <Section title="19. General">
            <ol style={{ listStyleType: 'lower-alpha' }}>
              <li>
                <span className="underline">No Joint Venture or Partnership.</span> No joint venture, partnership,
                employment, or agency relationship exists between you, Oriscen or any third-party
                provider as a result of this Agreement or use of the Services.
              </li>
              <li>
                <span className="underline">Choice of Law.</span> This Agreement is governed by the laws of the State
                of California consistent with the Federal Arbitration Act, without giving effect to
                any principles that provide for the application of the law of any other
                jurisdiction.
              </li>
              <li>
                <span className="underline">Severability.</span> Except as otherwise provided herein, if any
                provision of this Agreement is found to be invalid, the invalidity of such
                provision shall not affect the validity of the remaining provisions of this
                Agreement, which shall remain in full force and effect.
              </li>
              <li>
                <span className="underline">Electronic Communications.</span> For contractual purposes, you (1)
                consent to receive communications from us in an electronic form; and (2) agree that
                all terms and conditions, agreements, notices, disclosures, and other
                communications that we provide to you electronically satisfy any legal requirement
                that such communications would satisfy if they were in writing. This subparagraph
                does not affect your statutory rights.
              </li>
              <li>
                <span className="underline">Entire Agreement.</span> This Agreement is the final, complete and
                exclusive agreement of the parties with respect to the subject matter hereof and
                supersedes and merges all prior discussions between the parties with respect to
                such subject matter.
              </li>
              <li>
                <span className="underline">Waiver.</span> Our failure to enforce any right or provision of this
                Agreement will not be considered a waiver of such right or provision. The waiver of
                any such right or provision will be effective only if in writing and signed by a
                duly authorized representative of Oriscen. Except as expressly set forth in this
                Agreement, the exercise by either party of any of its remedies under this Agreement
                will be without prejudice to its other remedies under this Agreement or otherwise.
              </li>
            </ol>
          </Section>

          <Section title="20. Contact Information">
            <p>
              <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>
            </p>
            <p>
              Oriscen, Inc.
              <br />
              2261 Market Street STE 86857
              <br />
              San Francisco, CA 94114
            </p>
          </Section>
        </Prose>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2
        className="mb-4 text-lg font-semibold"
        style={{
          fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif',
          color: '#3A331D',
        }}
      >
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="space-y-4 text-base leading-relaxed text-ink/90 [&_a]:text-oriOrange [&_a]:underline [&_li]:my-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6"
      style={{ fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif' }}
    >
      {children}
    </div>
  );
}
